import dayjs from 'dayjs';
import parsePhoneNumber from 'libphonenumber-js';

import { ValidationErrorMessages } from '@constants/forms';
import { DOB_MAX_AGE_YEARS, DOB_MIN_AGE_YEARS } from '@constants/forms';
import {
  emailRegex,
  landlineRegex,
  mobileRegex,
  passwordRegex,
  postcodeRegex,
} from '@constants/regEx';
import { DateFormat } from '@src/types/index';

const validatePhoneNumberLib = (value: string) => {
  if (!value) return false;
  const phoneNumber = parsePhoneNumber(value, 'GB');
  return phoneNumber && phoneNumber.isValid();
};

const validateGBMobileNumber = (value: string) =>
  value === '' || mobileRegex.test(value);

const validateGBLandlineNumber = (value: string) => {
  if (value === '') {
    return true;
  }

  const removedWhitespace = value.replace(/\s+/g, '');
  return landlineRegex.test(removedWhitespace);
};

const validatePhoneNumber = (value: string) => {
  return (
    (validateGBMobileNumber(value) || validateGBLandlineNumber(value)) &&
    validatePhoneNumberLib(value)
  );
};

const validateEmail = (value: string) => emailRegex.test(value);

const validatePostcode = (postcode?: Nullable<string>) =>
  !!postcode && postcodeRegex.test(postcode.replace(/\s/g, ''));

const validateDob = (dateOfBirth: string): string | undefined => {
  if (!dateOfBirth) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (!dayjs(dateOfBirth, DateFormat.Numeric, true).isValid()) {
    return ValidationErrorMessages.INVALID_DATE;
  }

  if (
    dayjs(dateOfBirth, DateFormat.Numeric, true).isAfter(
      dayjs().subtract(DOB_MIN_AGE_YEARS, 'years'),
      'day'
    )
  ) {
    return ValidationErrorMessages.DOB_AGE_MIN;
  }

  if (
    dayjs(dateOfBirth, DateFormat.Numeric, true).isBefore(
      dayjs().subtract(DOB_MAX_AGE_YEARS, 'years'),
      'day'
    )
  ) {
    return ValidationErrorMessages.DOB_AGE_MAX;
  }

  return undefined;
};

const validateContactNumber = (contactNumber?: string): string | undefined => {
  if (!contactNumber) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (!validatePhoneNumber(contactNumber)) {
    return ValidationErrorMessages.INVALID_PHONE;
  }

  return undefined;
};

const validateEmailAddress = (
  email?: string,
  allowEmpty: boolean = false
): string | undefined => {
  if (!email && !allowEmpty) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (email && !validateEmail(email)) {
    return ValidationErrorMessages.INVALID_EMAIL;
  }

  return undefined;
};

const validateName = (name?: string): string | undefined => {
  if (!name || name.length <= 1) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }
  return undefined;
};

const validatePassword = (password?: string): string | undefined => {
  if (!password) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (password.length < 8) {
    return ValidationErrorMessages.PASSWORD_MIN_LENGTH;
  }

  if (!passwordRegex.test(password)) {
    return ValidationErrorMessages.PASSWORD_WRONG_FORMAT;
  }

  return undefined;
};

export {
  validateGBMobileNumber,
  validateGBLandlineNumber,
  validatePhoneNumber,
  validateEmail,
  validatePostcode,
  validateDob,
  validateContactNumber,
  validateEmailAddress,
  validateName,
  validatePassword,
};
